import _ from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import clearIcon from '../../icons/clear.svg';
import type { Emoji } from '../../utils/emoji';
import {
  applySkinToneToEmoji,
  EMOJI_CATEGORIES,
  emojiList,
  getEmojiItemByName,
  searchEmoji,
} from '../../utils/emoji';
import isSize from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import EmojiCategory from './EmojiCategory';
import styles from './EmojiPicker.module.scss';
import SkinToneSelector from './SkinToneSelector';

const useFrequentlyUsedEmojis = () => {
  const recentlyUsedEmojis = useAppSelector(
    (state) => state.ui.recentlyUsedEmojis,
  );

  const frequentlyUsedEmojis = useMemo(() => {
    const usages = _.countBy(recentlyUsedEmojis);
    return _.compact(
      Object.keys(usages)
        .sort((a, b) => (usages[b] ?? 0) - (usages[a] ?? 0))
        .map(getEmojiItemByName),
    );
  }, [recentlyUsedEmojis]);

  return frequentlyUsedEmojis;
};

const categoryEmojis: Record<string, Emoji[]> = {};
emojiList.forEach((emoji) => {
  if (!categoryEmojis[emoji.category]) categoryEmojis[emoji.category] = [];
  categoryEmojis[emoji.category]?.push(emoji);
});

const EMOJI_COUNT_FOR_TWO_LINES = 18;

const toneEmojis = (emojis: Emoji[] | null, modifier: string | null) =>
  emojis?.map((emoji) => ({
    emoji,
    tonedChar: applySkinToneToEmoji(emoji, modifier),
  }));

interface EmojiPickerProps {
  onPick: (emoji: string) => void;
  initialKeyword?: string;
}

const EmojiPicker = ({ onPick, initialKeyword = '' }: EmojiPickerProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [keyword, setKeyword] = useState(initialKeyword);
  const modifier = useAppSelector((state) => state.ui.emojiSkinToneModifier);
  const searchResults = toneEmojis(
    useMemo(() => (keyword ? searchEmoji(keyword) : null), [keyword]),
    modifier,
  );

  const frequentlyUsedEmojis = toneEmojis(
    useFrequentlyUsedEmojis().slice(0, EMOJI_COUNT_FOR_TWO_LINES),
    modifier,
  );

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (searchResults?.[0]) {
      onPick(searchResults[0].tonedChar);
    }
  };

  const isMobile = isSize(Sizes.SM);
  return (
    <div className={styles.container}>
      <form className={styles.searchContainer} onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          autoFocus={!isMobile}
          className={styles.searchInput}
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="Search emojis…"
          aria-label="Search emojis"
        />
        {keyword.length > 0 && (
          <Button
            className={styles.clearButton}
            size="small"
            icon={clearIcon}
            onClick={() => {
              setKeyword('');
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          />
        )}
      </form>
      <div className={styles.emojiContainer}>
        <SkinToneSelector />
        {searchResults ? (
          <EmojiCategory
            title="Search Results"
            items={searchResults}
            onPick={onPick}
          />
        ) : (
          <>
            {frequentlyUsedEmojis && frequentlyUsedEmojis.length > 0 && (
              <EmojiCategory
                title="Frequently Used"
                items={frequentlyUsedEmojis}
                onPick={onPick}
              />
            )}
            {EMOJI_CATEGORIES.map((category) => (
              <EmojiCategory
                key={category}
                title={_.capitalize(category.replace(/_/g, ' '))}
                items={
                  toneEmojis(categoryEmojis[category] ?? null, modifier) ?? []
                }
                onPick={onPick}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default EmojiPicker;
